<template>
  <!--
    AB#21807 — the Comms mood badge in the app bar. The last 7 days' face, and a small
    dot when today reads "worth a look". Click opens the drawer. Rendered only for
    someone holding view-own-communication-mood AND while the feature is on — the
    backend answers {enabled:false} otherwise and the badge never appears.
  -->
  <button
    v-if="summary"
    type="button"
    class="cm-badge"
    :title="title"
    :aria-label="title"
    data-test="comms-mood-badge"
    @click="open"
  >
    <MoodFace :level="summary.faces.week.level" :size="24" />
    <span v-if="dot" class="cm-badge-dot" data-test="comms-mood-dot" aria-hidden="true"></span>
  </button>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue';
import { can } from '@/composables';
import MoodFace from './MoodFace.vue';
import {
  OWN_PERMISSION,
  clearMoodCache,
  needsLook,
  openMoodDrawer,
  useOwnMood,
} from '@/composables/useCommsMood';

const { summary, load } = useOwnMood();
const allowed = computed(() => can(OWN_PERMISSION));

const dot = computed(() => (summary.value ? needsLook(summary.value.faces.today.level) : false));
const title = computed(() => {
  if (!summary.value) return 'Comms mood';
  const f = summary.value.faces;
  return `Comms mood — last 7 days: ${f.week.label} · today: ${f.today.label}`;
});

const open = (): void => openMoodDrawer({ period: 'week' });

// Same trick as the Needs Attention badge: permissions resolve after the header
// mounts, so start when the permission arrives, not in onMounted. The mood moves
// slowly (debounced ten minutes server-side), so a five-minute poll is plenty.
let poll: ReturnType<typeof setInterval> | null = null;
const start = (): void => {
  if (poll !== null) return;
  void load();
  poll = setInterval(() => {
    clearMoodCache();
    void load();
  }, 300000);
};
watch(allowed, (ok) => ok && start(), { immediate: true });
onBeforeUnmount(() => {
  if (poll !== null) clearInterval(poll);
});
</script>

<style scoped>
/* Sits on the always-dark app bar beside the Needs Attention pill, in its calm style. */
.cm-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.08);
  color: #cfd4da;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.cm-badge:hover {
  background: rgba(255, 255, 255, 0.16);
}
.cm-badge:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}
.cm-badge-dot {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--mood-3);
  box-shadow: 0 0 0 2px #191919;
}
</style>
